import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector, useDispatch } from 'react-redux';
import { io } from 'socket.io-client';

import { getRtmToken } from '@/api/client';
import { createCampaignListReconciler } from '@/modules/campaigns/api';
import { Socket, SocketEvent } from '@/shared/types/rtm';
import { USER_ID_MOCK } from '@/util/constants';

import { accountBindings } from './accountBindings';
import { resyncOnReconnect } from './resyncOnReconnect';
import { useRtmBindings } from './rtmBindings';
import {
  markAgentOffline,
  markAgentOnline,
  removeViewer,
  setAgents,
  setAllViewers,
  setViewers,
} from '../../redux/presence/actions';
import { selectAccountId } from '../../redux/session/selectors';
import { selectUser } from '../../redux/user/selectors';
import { useDeskId } from '../useDeskId';

const SERVER_OR_CLIENT_DISCONNECT = new RegExp(/io server/);

const TEN_SECONDS = 10000;

export const useRtmSetup = () => {
  const user = useSelector(selectUser);
  const account_id = useSelector(selectAccountId);
  const dispatch = useDispatch();
  const userId = user?.user_id;
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | undefined>();
  const [isConnected, setIsConnected] = useState(false);
  const deskId = useDeskId();

  // Coalesces a campaigns_removed burst (another user's bulk delete) into one reconciling
  // refetch — see createCampaignListReconciler. Cache surgery still runs per event.
  const campaignListReconciler = useMemo(
    () => createCampaignListReconciler(queryClient),
    [queryClient]
  );
  useEffect(() => () => campaignListReconciler.cancel(), [campaignListReconciler]);

  const bindings = useMemo(() => accountBindings(campaignListReconciler), [campaignListReconciler]);
  useRtmBindings(socket, bindings);

  useEffect(() => {
    //Establish a socket connection when the deskId is added in redux session
    if (!socket && queryClient && deskId) {
      const client = io(window.RTM_URL, {
        autoConnect: false,
        reconnection: true,
        path: '/ws',
        transports: ['websocket'],
        // A capped retry gives up after about two minutes offline and leaves the tab without events.
        reconnectionAttempts: Infinity,
        auth: (cb) =>
          getRtmToken()
            .then(cb)
            .catch(() => cb({ token: 'ignore-token' })),
      }) as Socket;

      client.on('error' as never, (error) => {
        console.debug('socket.error', error);
      });
      client.on('connect_error', (error) => {
        console.debug('socket.connect_error', error);
      });

      const onConnect = resyncOnReconnect(queryClient);
      client.on('connect', () => {
        console.debug('socket.connect', `${client.id} at ${Date.now()}`);
        setIsConnected(true);
        onConnect(client.recovered);
      });

      client.on('disconnect', (reason) => {
        console.debug('socket.reconnect reason:', reason);
        setIsConnected(false);

        if (SERVER_OR_CLIENT_DISCONNECT.test(reason)) {
          // The disconnection was initiated by the server or the client, reconnect manually.
          // For all the other reasons the socket will automatically try to reconnect
          client.connect();
        }
      });

      client.on(SocketEvent.collections_viewing, ({ data }) => {
        const { collection_id, agents } = data;
        dispatch(setViewers({ type: 'collections', id: collection_id, agents: agents }));
      });

      client.on(SocketEvent.collections_viewing_stop, ({ user_id, data }) => {
        const { collection_id } = data;
        dispatch(removeViewer({ type: 'collections', id: collection_id, user_id }));
      });

      client.on(SocketEvent.collections_viewers, ({ data }) => {
        const { resources } = data;
        dispatch(setAllViewers({ type: 'collections', resources }));
      });

      client.on(SocketEvent.brains_viewing, ({ data }) => {
        const { brain_id, agents } = data;
        dispatch(setViewers({ type: 'brains', id: brain_id, agents: agents }));
      });

      client.on(SocketEvent.brains_viewing_stop, ({ user_id, data }) => {
        const { brain_id } = data;
        dispatch(removeViewer({ type: 'brains', id: brain_id, user_id }));
      });

      client.on(SocketEvent.brains_viewers, ({ data }) => {
        const { resources } = data;
        dispatch(setAllViewers({ type: 'brains', resources }));
      });

      client.on(SocketEvent.guidelines_viewing, ({ data }) => {
        const { brain_id, agents } = data;
        dispatch(setViewers({ type: 'guidelines', id: brain_id, agents }));
      });

      client.on(SocketEvent.guidelines_viewing_stop, ({ user_id, data }) => {
        const { brain_id } = data;
        dispatch(removeViewer({ type: 'guidelines', id: brain_id, user_id }));
      });

      client.on(SocketEvent.guidelines_viewers, ({ data }) => {
        const { resources } = data;
        dispatch(setAllViewers({ type: 'guidelines', resources }));
      });

      client.on(SocketEvent.conversations_viewers, ({ data }) => {
        const { resources } = data;
        dispatch(setAllViewers({ type: 'sessions', resources }));
      });

      client.on(SocketEvent.agent_presence, ({ data }) => dispatch(setAgents(data)));
      client.on(SocketEvent.agent_online, ({ data }) => dispatch(markAgentOnline(data)));

      client.on(SocketEvent.agent_offline, ({ data }) => dispatch(markAgentOffline(data)));

      //TODO Uncomment this so we can show when a user visited a brain
      // client.on(SocketEvent.agent_activity, (data) => {
      //   // skip self because it creates a ton of unnecessary rerenders
      //   if (data.user_id !== userId) {
      //     dispatch(updateAgentActivity(data));
      //   }
      // });

      client.onAny((event, data) => console.debug(event, data));

      setSocket(client);
    }
  }, [deskId, dispatch, queryClient, socket]);

  const onAction = useCallback(() => {
    if (!socket || !userId || !account_id) {
      return;
    }
    socket?.volatile.emit(SocketEvent.agent_activity, {
      account_id,
      user_id: userId,
      timestamp: Date.now(),
    });
  }, [account_id, socket, userId]);

  useIdleTimer({ onAction, throttle: TEN_SECONDS, events: ['mousemove', 'keydown'] });

  // Connect to the websocket on component mount and disconnect on unmount
  useEffect(() => {
    if (userId && userId !== USER_ID_MOCK) {
      socket?.connect();
    }

    return () => {
      socket?.disconnect();
    };
  }, [socket, userId]);

  return { socket, isConnected };
};
